import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import * as THREE from 'three';
import useEditorStore from './useEditorStore';
import useFinalizationDataStore from './useFinalizationDataStore';

/**
 * Brush smoothening for the Rectification step (Carve Relief).
 *
 * Same shape as the flares / ML-reduction smoothening stores so it can drive the
 * shared `ZoneSmoothningToolCard` and `ZoneSmootheningHandler`. It is kept apart
 * from `useRectificationStore.smoothning` — that state belongs to the Prepare
 * Scan smoothing tool, which keeps its legacy `main_rectification_*` id.
 */
const useCarveSmootheningStore = create(
  devtools((set, get) => ({
    smoothning: {
      enabled: true,
      zones: [],
      brushRadius: 0.75,
      previewVisible: true,
      previewMesh: null,
    },

    setSmoothning: (data) => set({ smoothning: { ...get().smoothning, ...data } }),

    isSmootheningApplied: false,
    setIsSmootheningApplied: (status) => set({ isSmootheningApplied: status }),
    isSmootheningAppliedFromDraft: false,
    setIsSmootheningAppliedFromDraft: (status) => set({ isSmootheningAppliedFromDraft: status }),

    currentSmootheningCommand: null,
    setCurrentSmootheningCommand: (command) => set({ currentSmootheningCommand: command }),

    createSmootheningCommand: () => {
      const { smoothning } = get();
      const { sceneRefs, replaceShadedMesh } = useEditorStore.getState();

      if (smoothning.zones.length === 0 || !smoothning.previewMesh) return null;

      const originalMesh = sceneRefs.shadedMesh;
      const originalGeometry = originalMesh.geometry;
      const originalMaterial = sceneRefs.material;
      const originalMatrix = originalMesh.matrix.clone();

      const originalSmoothning = {
        ...smoothning,
        zones: (smoothning.zones || []).map((zone) => ({
          ...zone,
          influences: zone.influences ? new Float32Array(zone.influences) : zone.influences,
        })),
      };

      const cloneGeom = new THREE.BufferGeometry().copy(smoothning.previewMesh.geometry);
      const cloneMatrix = new THREE.Matrix4().copy(smoothning.previewMesh.matrix);

      return {
        execute: () => {
          const latest = get().smoothning;
          const preview = latest?.previewMesh;
          if (preview && preview.isMesh) {
            preview.parent?.remove(preview);
            preview.geometry?.dispose?.();
            preview.material?.dispose?.();
            preview.userData?.baseGeometry?.dispose?.();
          }

          const smoothedMesh = new THREE.Mesh(cloneGeom, originalMaterial);
          smoothedMesh.matrix.copy(cloneMatrix);
          smoothedMesh.matrix.decompose(smoothedMesh.position, smoothedMesh.quaternion, smoothedMesh.scale);
          smoothedMesh.matrixAutoUpdate = true;
          replaceShadedMesh(smoothedMesh);

          useFinalizationDataStore.getState().saveRectificationSmoothening(originalSmoothning.zones);

          set({
            isSmootheningApplied: true,
            smoothning: {
              zones: [],
              previewVisible: false,
              previewMesh: null,
              enabled: true,
              brushRadius: originalSmoothning.brushRadius,
            },
          });
        },
        undo: () => {
          const restoredMesh = new THREE.Mesh(originalGeometry, originalMaterial);
          restoredMesh.matrix.copy(originalMatrix);
          restoredMesh.matrix.decompose(restoredMesh.position, restoredMesh.quaternion, restoredMesh.scale);
          restoredMesh.matrixAutoUpdate = true;
          replaceShadedMesh(restoredMesh);

          set({
            isSmootheningApplied: false,
            smoothning: {
              ...originalSmoothning,
              previewMesh: null,
            },
          });
        },
      };
    },

    reset: () => set({
      smoothning: {
        enabled: true,
        zones: [],
        brushRadius: 1.0,
        previewVisible: true,
        previewMesh: null,
      },
      isSmootheningApplied: false,
      isSmootheningAppliedFromDraft: false,
      currentSmootheningCommand: null,
    }),
  })),
  {
    name: 'carve-smoothening-store',
  }
);

export default useCarveSmootheningStore;
